import {GameEvent, GamePayload, isPlayerAction, PlayerAction, PlayerId} from '@glark-newco/game-library';
import {AvatarInterface} from './hooks/useCreateAvatar';

export enum PayloadTypes { 'assignPlayerNode' }

export type PayloadType = typeof PayloadTypes[number];

interface Payload extends GamePayload {
    type: PayloadType;
}

export interface PlayerAvatarChoiceEvent extends Payload {
    type: 'playerAvatarChoiceEvent';
    avatar: AvatarInterface;
}

export type RingId = string;
export enum RingColour {RED = 'RED', GREEN = 'GREEN', YELLOW = 'YELLOW', BLUE = 'BLUE'}
export interface RingDefinition {
    ringId: RingId;
    colour: RingColour;
}

export interface AssignPlayerNodePayload extends Payload {
    type: 'assignPlayerNode';
    playerId: PlayerId; // Player being placed.  Not the player performing the action.
    x: number;
    y: number;
    rings: RingDefinition[];
    objective: RingColour[];
}

export type LinkRelationship = 'local' | 'remote';
export interface AssignLinkEvent extends Payload {
    type: 'assignLink';
    playerA: PlayerId;
    playerB: PlayerId;
    relationship: LinkRelationship;
}

export interface DisplayPlayScenePayload extends Payload {
    type: 'displayPlayScene';
}

export interface TransferRingEvent extends Payload {
    type: 'transferRing';
    ringId: RingId;
    sourcePlayerId: PlayerId;
    recipientPlayerId: PlayerId;
}

export interface SaveObjectiveEvent extends Payload {
    type: 'saveObjective';
    ringId: RingId;
    playerId: PlayerId; // Player receiving the objective.
}


export interface StartTimerEvent extends Payload {
    type: 'startTimerEvent';
    duration: number; // minutes
}

export interface ReviewStatsPayload extends Payload {
    type: 'reviewStats';
}

export type RingPayload = AssignPlayerNodePayload | SaveObjectiveEvent | TransferRingEvent;
export type ObjectivePayload = AssignPlayerNodePayload | SaveObjectiveEvent;
export type SupplyChainPayload = PlayerAvatarChoiceEvent | RingPayload | ObjectivePayload | AssignLinkEvent | DisplayPlayScenePayload | StartTimerEvent | ReviewStatsPayload;

export interface SupplyChainEvent<T extends SupplyChainPayload> extends PlayerAction {
    payload: T;
}

export function isPlayerAvatarPayload(payload: GamePayload): payload is PlayerAvatarChoiceEvent {
    return payload?.type == 'playerAvatarChoiceEvent';
}

export function isAssignPlayerNodePayload(payload: GamePayload): payload is AssignPlayerNodePayload {
    return payload?.type == 'assignPlayerNode';
}

export function isAssignLinkPayload(payload: GamePayload): payload is AssignLinkEvent {
    return payload?.type == 'assignLink';
}

export function isDisplayPlayScenePayload(payload: GamePayload): payload is DisplayPlayScenePayload {
    return payload?.type == 'displayPlayScene';
}

export function isTransferRingEvent(payload: GamePayload): payload is TransferRingEvent {
    return payload?.type == 'transferRing';
}

export function isSaveObjectivePayload(payload: GamePayload): payload is SaveObjectiveEvent {
    return payload?.type == 'saveObjective';
}

export function isStartTimerPayload(payload: GamePayload): payload is StartTimerEvent {
    return payload?.type == 'startTimerEvent';
}

export function isReviewStatsPayload(payload: GamePayload): payload is ReviewStatsPayload {
    return payload?.type == 'reviewStats';
}

export function isRingPayloadEvent(payload: GamePayload): payload is RingPayload {
    return isAssignPlayerNodePayload(payload) || isTransferRingEvent(payload) || isSaveObjectivePayload(payload);
}

export function isAssignLinkEvent(payload: GamePayload): payload is RingPayload {
    return isAssignPlayerNodePayload(payload) || isAssignLinkPayload(payload);
}

export function isObjectivePayload(payload: GamePayload): payload is ObjectivePayload {
    return isAssignPlayerNodePayload(payload) || isSaveObjectivePayload(payload);
}

export function isAssignPlayerNodeEvent(event: GameEvent): event is SupplyChainEvent<AssignPlayerNodePayload> {
    return isPlayerAction(event) && isAssignPlayerNodePayload(event.payload);
}

export function isDisplayPlaySceneEvent(event: GameEvent): event is SupplyChainEvent<DisplayPlayScenePayload> {
    return isPlayerAction(event) && isDisplayPlayScenePayload(event.payload);
}

export function isReviewStatsEvent(event: GameEvent): event is SupplyChainEvent<ReviewStatsPayload> {
    return isPlayerAction(event) && isReviewStatsPayload(event.payload);
}

export function isSaveObjectiveEvent(event: GameEvent): event is SupplyChainEvent<SaveObjectiveEvent> {
    return isPlayerAction(event) && isSaveObjectivePayload(event.payload);
}

export function isObjectiveEvent(event: GameEvent): event is SupplyChainEvent<ObjectivePayload> {
    return isPlayerAction(event) && isObjectivePayload(event.payload);
}
